
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import AllowanceDeductionFields from './AllowanceDeductionFields';
import { X, Plus } from 'lucide-react';

interface PaymentFormProps {
  initialData: {
    amount: number;
    allowances: number | any[];
    deductions: number | any[];
    paymentMethod: string;
    notes: string;
    paymentPeriod: string;
  };
  onSubmit: (formData: any) => void;
  onCancel: () => void;
}

export const PaymentForm = ({ initialData, onSubmit, onCancel }: PaymentFormProps) => {
  const [formData, setFormData] = useState({
    amount: initialData.amount || 0,
    paymentMethod: initialData.paymentMethod || 'bank_transfer',
    notes: initialData.notes || '',
    paymentPeriod: initialData.paymentPeriod || format(new Date(), 'MMMM yyyy'),
    allowances: Array.isArray(initialData.allowances) ? initialData.allowances : [],
    deductions: Array.isArray(initialData.deductions) ? initialData.deductions : []
  });

  const [totalAllowances, setTotalAllowances] = useState<number>(
    Array.isArray(initialData.allowances) 
      ? initialData.allowances.reduce((sum: number, item: any) => sum + (Number(item.amount) || 0), 0)
      : Number(initialData.allowances) || 0
  );

  const [totalDeductions, setTotalDeductions] = useState<number>(
    Array.isArray(initialData.deductions) 
      ? initialData.deductions.reduce((sum: number, item: any) => sum + (Number(item.amount) || 0), 0)
      : Number(initialData.deductions) || 0
  );

  const [allowanceItem, setAllowanceItem] = useState({ description: '', amount: '' });
  const [deductionItem, setDeductionItem] = useState({ description: '', amount: '' });

  const calculateNetAmount = () => {
    const baseAmount = Number(formData.amount);
    return baseAmount + totalAllowances - totalDeductions;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentMethodChange = (method: string) => {
    setFormData(prev => ({
      ...prev,
      paymentMethod: method
    }));
  };

  const handleAddAllowance = () => {
    if (!allowanceItem.description || !allowanceItem.amount || isNaN(Number(allowanceItem.amount))) {
      return;
    }
    
    const amount = Number(allowanceItem.amount);
    const newAllowance = {
      id: crypto.randomUUID(),
      description: allowanceItem.description,
      amount: amount,
      type: allowanceItem.description // For compatibility with existing code
    };
    
    setFormData(prev => ({
      ...prev,
      allowances: [...prev.allowances, newAllowance]
    }));
    
    setTotalAllowances(prev => prev + amount);
    setAllowanceItem({ description: '', amount: '' });
  };

  const handleRemoveAllowance = (id: string, amount: number) => {
    setFormData(prev => ({
      ...prev,
      allowances: prev.allowances.filter((item: any) => item.id !== id)
    }));
    
    setTotalAllowances(prev => prev - amount);
  };

  const handleAddDeduction = () => {
    if (!deductionItem.description || !deductionItem.amount || isNaN(Number(deductionItem.amount))) {
      return;
    }
    
    const amount = Number(deductionItem.amount);
    const newDeduction = {
      id: crypto.randomUUID(),
      description: deductionItem.description,
      amount: amount,
      type: deductionItem.description // For compatibility with existing code
    };
    
    setFormData(prev => ({
      ...prev,
      deductions: [...prev.deductions, newDeduction]
    }));
    
    setTotalDeductions(prev => prev + amount);
    setDeductionItem({ description: '', amount: '' });
  };

  const handleRemoveDeduction = (id: string, amount: number) => {
    setFormData(prev => ({
      ...prev,
      deductions: prev.deductions.filter((item: any) => item.id !== id)
    }));
    
    setTotalDeductions(prev => prev - amount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const paymentData = {
      ...formData,
      amount: Number(formData.amount),
      allowances: formData.allowances,
      deductions: formData.deductions,
      totalAllowances,
      totalDeductions,
      netAmount: calculateNetAmount() // Add net amount to submission
    };
    
    onSubmit(paymentData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="amount">Base Amount (AFN)</Label>
        <Input
          id="amount"
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleInputChange}
          required
          className="mt-1"
          aria-label="Base Salary Amount"
        />
      </div>

      <div className="border rounded-md p-4">
        <h4 className="font-medium mb-2">Allowances</h4>
        
        {formData.allowances.length > 0 && (
          <div className="space-y-2 mb-4">
            {formData.allowances.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
                <span>{item.description || item.type}</span>
                <div className="flex items-center">
                  <span className="mr-2">{item.amount} AFN</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRemoveAllowance(item.id, item.amount)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="text-right font-medium">
              Total Allowances: {totalAllowances} AFN
            </div>
          </div>
        )}
        
        <div className="flex space-x-2">
          <Input
            placeholder="Description"
            value={allowanceItem.description}
            onChange={(e) => setAllowanceItem(prev => ({ ...prev, description: e.target.value }))}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="Amount"
            value={allowanceItem.amount}
            onChange={(e) => setAllowanceItem(prev => ({ ...prev, amount: e.target.value }))}
            className="w-24"
          />
          <Button type="button" onClick={handleAddAllowance} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="border rounded-md p-4">
        <h4 className="font-medium mb-2">Deductions</h4>
        
        {formData.deductions.length > 0 && (
          <div className="space-y-2 mb-4">
            {formData.deductions.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
                <span>{item.description || item.type}</span>
                <div className="flex items-center">
                  <span className="mr-2">{item.amount} AFN</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRemoveDeduction(item.id, item.amount)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="text-right font-medium">
              Total Deductions: {totalDeductions} AFN
            </div>
          </div>
        )}
        
        <div className="flex space-x-2">
          <Input
            placeholder="Description"
            value={deductionItem.description}
            onChange={(e) => setDeductionItem(prev => ({ ...prev, description: e.target.value }))}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="Amount"
            value={deductionItem.amount}
            onChange={(e) => setDeductionItem(prev => ({ ...prev, amount: e.target.value }))}
            className="w-24"
          />
          <Button type="button" onClick={handleAddDeduction} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
        <div className="flex justify-between items-center">
          <span className="font-medium">Net Payment Amount:</span>
          <span className="text-xl font-bold">{calculateNetAmount()} AFN</span>
        </div>
      </div>

      <div>
        <Label htmlFor="paymentPeriod">Payment Period</Label>
        <Input
          id="paymentPeriod"
          name="paymentPeriod"
          value={formData.paymentPeriod}
          onChange={handleInputChange}
          className="mt-1"
        />
      </div>

      <div>
        <Label>Payment Method</Label>
        <PaymentMethodSelector
          value={formData.paymentMethod}
          onChange={handlePaymentMethodChange}
        />
      </div>

      <div>
        <Label htmlFor="notes">Payment Notes</Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          placeholder="Add any payment notes here..."
          className="mt-1"
          rows={3}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Process Payment
        </Button>
      </div>
    </form>
  );
};
